<script setup lang="ts">
import { app as appInfo } from '@shared/lib/app'
import { onMounted, ref } from 'vue'
import { ApiError, exchangeHandoff, getCurrentAttempt, getInfo, getScheduleByCredentials, getScheduleMe, getSessions, login, loginUnified, startAttempt } from './api'
import ConfirmView from './components/ConfirmView.vue'
import ExamView from './components/ExamView.vue'
import LoginView from './components/LoginView.vue'
import ResultView from './components/ResultView.vue'
import ScheduleView from './components/ScheduleView.vue'
import PhaseStatus from './components/PhaseStatus.vue'
import type { Phase } from './lib/phase'
import { takeHandoffToken } from './lib/handoff'
import { goToStaffScreen, isStaffLogin } from './lib/staffLogin'
import { retryDelayJittered } from './lib/time'
import { clearSession, loadSession, saveSession, type StoredSession } from './lib/session'
import type { AttemptView, InfoResponse, LoginPayload, LoginResponse, MySchedule, SessionChoice } from './types'

type Stage = 'booting' | 'login' | 'confirm' | 'exam' | 'result' | 'schedule' | 'error'

const stage = ref<Stage>('booting')
const info = ref<InfoResponse | null>(null)
const session = ref<StoredSession | null>(null)
const view = ref<AttemptView | null>(null)
const busy = ref(false)
const loginError = ref('')
const notice = ref('')
const bootError = ref('')
const phase = ref<Phase | null>(null)
const phaseSince = ref(Date.now())
const sessionChoices = ref<SessionChoice[] | null>(null)
const retryUntil = ref<number | null>(null)

// The pupil's own schedule. The credentials are kept in memory only, for the minutes this screen is
// open, so "Mulai ujian" can sign in without typing them again; never stored.
const schedule = ref<MySchedule | null>(null)
const scheduleMode = ref<'credentials' | 'token'>('credentials')
const scheduleNotice = ref('')
let scheduleCreds: { identifier: string; secret: string } | null = null

function setPhase(next: Phase | null): void {
  phase.value = next
  phaseSince.value = Date.now()
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Terjadi kesalahan yang tidak dikenal.'
}

function applyView(next: AttemptView): void {
  view.value = next
  if (info.value && next.exam) {
    info.value = { ...info.value, exam: next.exam, item_count: next.exam.item_count }
  }
  stage.value = next.attempt.status === 'submitted' ? 'result' : 'exam'
}

function expireSession(message: string): void {
  clearSession()
  session.value = null
  view.value = null
  notice.value = message
  loginError.value = ''
  stage.value = 'login'
}

async function bootstrap(): Promise<void> {
  stage.value = 'booting'
  bootError.value = ''
  try {
    info.value = await getInfo()
  } catch (error) {
    bootError.value = messageFor(error)
    stage.value = 'error'
    return
  }

  // Arriving from the management portal: the token in the address is exchanged for a session here.
  const handoffToken = takeHandoffToken()
  if (handoffToken) {
    setPhase('logging_in')
    try {
      const result = (await exchangeHandoff(handoffToken)) as unknown as LoginResponse
      const entered: StoredSession = {
        token: result.token,
        participant: result.participant,
        exam: result.exam,
        session: result.session,
        saved_at: new Date().toISOString(),
      }
      saveSession(entered)
      session.value = entered
      if (info.value) info.value = { ...info.value, exam: result.exam, item_count: result.exam.item_count }
      if (result.attempt_status === 'submitted') applyView(await getCurrentAttempt(entered.token))
      else stage.value = 'confirm'
    } catch (error) {
      loginError.value = messageFor(error)
      stage.value = 'login'
    } finally {
      setPhase(null)
    }
    return
  }

  const stored = loadSession()
  if (!stored) {
    stage.value = 'login'
    return
  }
  session.value = stored
  setPhase('loading_exam')
  try {
    applyView(await getCurrentAttempt(stored.token))
    setPhase(null)
  } catch (error) {
    setPhase(null)
    if (error instanceof ApiError && error.status === 404) {
      // No attempt yet (first visit before confirming, or a retake): never start it silently.
      // Starting is the participant's own act on the confirm page (rules accepted, room token).
      loginError.value = ''
      stage.value = 'confirm'
      return
    }
    if (error instanceof ApiError && error.status === 401) {
      expireSession(error.code === 'device_replaced' ? error.message : 'Sesi berakhir. Silakan masuk kembali.')
      return
    }
    loginError.value = messageFor(error)
    stage.value = 'login'
  }
}

// During a spike the start request can fail transiently. Retry a few times with jitter, then hand
// the decision back to the student (the login form stays usable).
const START_RETRIES = 3

async function startAttemptWithRetry(token: string, examToken?: string): Promise<AttemptView> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await startAttempt(token, examToken)
    } catch (error) {
      const transient =
        !(error instanceof ApiError) || error.status === 429 || error.status >= 500
      if (!transient || attempt > START_RETRIES) throw error
      const wait =
        error instanceof ApiError && error.retryAfterSeconds
          ? error.retryAfterSeconds * 1000
          : retryDelayJittered(attempt)
      await new Promise((resolve) => setTimeout(resolve, wait))
    }
  }
}

async function handleLogin(payload: LoginPayload): Promise<void> {
  if (busy.value) return
  busy.value = true
  loginError.value = ''
  notice.value = ''
  retryUntil.value = null
  setPhase('logging_in')
  try {
    const result =
      payload.kind === 'code'
        ? await login(payload.access_code)
        : await loginUnified({
            identifier: payload.identifier,
            secret: payload.secret,
            session_id: payload.session_id,
          })
    // Staff share this form; they continue on their own screen.
    if (isStaffLogin(result)) {
      goToStaffScreen(result.role, result.token)
      return
    }
    sessionChoices.value = null
    scheduleCreds = null
    schedule.value = null
    const stored: StoredSession = {
      token: result.token,
      participant: result.participant,
      exam: result.exam,
      session: result.session,
      saved_at: new Date().toISOString(),
    }
    saveSession(stored)
    session.value = stored
    if (info.value) {
      info.value = { ...info.value, exam: result.exam, item_count: result.exam.item_count }
    }
    // A participant who already finished has nothing to start: show the result page (which only
    // shows the score when the exam allows it) without asking for the token again.
    if (result.attempt_status === 'submitted') {
      applyView(await getCurrentAttempt(stored.token))
      return
    }
    // Show the participant and exam details first; the attempt starts only on confirmation.
    stage.value = 'confirm'
  } catch (error) {
    if (error instanceof ApiError && payload.kind === 'unified' && (error.code === 'session_required' || error.code === 'exam_unavailable')) {
      // Several exams, or none open yet: show the pupil their own schedule instead of an error.
      const shown = await showSchedule({ identifier: payload.identifier, secret: payload.secret })
      if (!shown) {
        if (error.code === 'session_required') await offerSessions()
        else loginError.value = messageFor(error)
      }
    } else if (error instanceof ApiError && error.status === 409 && error.code === 'session_required') {
      await offerSessions()
    } else if (error instanceof ApiError && error.status === 429) {
      // The countdown text lives in LoginView; no error banner on top of it.
      retryUntil.value = Date.now() + (error.retryAfterSeconds ?? 5) * 1000
    } else {
      loginError.value = messageFor(error)
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        clearSession()
        session.value = null
      }
    }
  } finally {
    setPhase(null)
    busy.value = false
  }
}

async function confirmStart(examToken?: string): Promise<void> {
  if (busy.value || !session.value) return
  busy.value = true
  loginError.value = ''
  setPhase('loading_exam')
  try {
    applyView(await startAttemptWithRetry(session.value.token, examToken))
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      expireSession(error.code === 'device_replaced' ? error.message : 'Sesi berakhir. Silakan masuk kembali.')
    } else {
      loginError.value = messageFor(error)
    }
  } finally {
    setPhase(null)
    busy.value = false
  }
}

function cancelConfirm(): void {
  clearSession()
  session.value = null
  loginError.value = ''
  notice.value = ''
  stage.value = 'login'
}

async function offerSessions(): Promise<void> {
  try {
    const list = await getSessions()
    if (list.sessions.length === 0) {
      loginError.value = 'Tidak ada sesi ujian yang tersedia. Hubungi pengawas.'
      return
    }
    sessionChoices.value = list.sessions
  } catch (error) {
    loginError.value = messageFor(error)
  }
}

async function showSchedule(creds: { identifier: string; secret: string }, text = ''): Promise<boolean> {
  busy.value = true
  try {
    schedule.value = await getScheduleByCredentials(creds)
    scheduleMode.value = 'credentials'
    scheduleCreds = creds
    scheduleNotice.value = text
    loginError.value = ''
    stage.value = 'schedule'
    return true
  } catch (error) {
    if (error instanceof ApiError && error.status === 429) {
      retryUntil.value = Date.now() + (error.retryAfterSeconds ?? 5) * 1000
    } else if (stage.value === 'schedule') {
      // A refresh that failed keeps the schedule on screen and says so.
      loginError.value = messageFor(error)
    } else {
      loginError.value = messageFor(error)
    }
    return false
  } finally {
    busy.value = false
  }
}

function handleScheduleRequest(payload: LoginPayload): void {
  if (busy.value || payload.kind !== 'unified') return
  loginError.value = ''
  void showSchedule({ identifier: payload.identifier, secret: payload.secret })
}

/** The result screen asks for the schedule with the pupil's token; "Mulai" is not offered there. */
async function showMySchedule(): Promise<void> {
  if (!session.value || busy.value) return
  busy.value = true
  try {
    schedule.value = await getScheduleMe(session.value.token)
    scheduleMode.value = 'token'
    scheduleCreds = null
    scheduleNotice.value = 'Untuk mengerjakan ujian berikutnya, masuk kembali dengan ID dan kata sandi Anda.'
    loginError.value = ''
    stage.value = 'schedule'
  } catch (error) {
    loginError.value = messageFor(error)
  } finally {
    busy.value = false
  }
}

async function refreshSchedule(): Promise<void> {
  if (busy.value) return
  if (scheduleMode.value === 'credentials' && scheduleCreds) {
    await showSchedule(scheduleCreds, scheduleNotice.value)
  } else if (session.value) {
    try {
      schedule.value = await getScheduleMe(session.value.token)
    } catch {
      // Keep what is on screen.
    }
  }
}

async function startFromSchedule(sessionId: string): Promise<void> {
  if (!scheduleCreds) return
  await handleLogin({ kind: 'unified', identifier: scheduleCreds.identifier, secret: scheduleCreds.secret, session_id: sessionId })
}

function leaveSchedule(): void {
  loginError.value = ''
  if (scheduleMode.value === 'token') {
    stage.value = 'result'
    return
  }
  scheduleCreds = null
  schedule.value = null
  stage.value = 'login'
}

function cancelSessionChoice(): void {
  sessionChoices.value = null
  loginError.value = ''
}

function handleSubmitted(next: AttemptView): void {
  info.value = info.value ? { ...info.value, exam: next.exam } : info.value
  view.value = next
  stage.value = 'result'
}

function handleUnauthorized(message?: string): void {
  expireSession(message ?? 'Sesi tidak valid atau sudah kedaluwarsa. Silakan masuk kembali.')
}

function logout(): void {
  if (!window.confirm('Anda yakin ingin keluar dari sesi ujian?')) return
  clearSession()
  session.value = null
  view.value = null
  loginError.value = ''
  notice.value = 'Anda sudah keluar dari sesi ujian.'
  stage.value = 'login'
}

onMounted(() => {
  void bootstrap()
})
</script>

<template>
  <a class="skip-link" href="#main-content">Lewati ke konten utama</a>

  <div v-if="stage === 'booting'" class="screen-center" role="status" aria-live="polite">
    <div class="boot-card">
      <div class="brand-mark brand-mark--large" aria-hidden="true">OAP</div>
      <h1>Menyiapkan {{ appInfo.name }}</h1>
      <PhaseStatus v-if="phase" :phase="phase" :since="phaseSince" reserve />
      <p v-else>Memeriksa paket ujian dan sesi peserta…</p>
      <div class="boot-bar" aria-hidden="true"><span /></div>
    </div>
  </div>

  <LoginView
    v-else-if="stage === 'login'"
    :busy="busy"
    :error="loginError"
    :notice="notice"
    :phase="phase"
    :phase-since="phaseSince"
    :sessions="sessionChoices"
    :retry-until="retryUntil"
    @submit="handleLogin"
    @schedule="handleScheduleRequest"
    @cancel-session-choice="cancelSessionChoice"
  />

  <ScheduleView
    v-else-if="stage === 'schedule' && schedule"
    :schedule="schedule"
    :can-start="scheduleMode === 'credentials'"
    :busy="busy"
    :notice="scheduleNotice"
    :error="loginError"
    :auth-token="session?.token"
    :back-label="scheduleMode === 'credentials' ? 'Kembali ke halaman masuk' : 'Kembali'"
    @start="startFromSchedule"
    @refresh="refreshSchedule"
    @back="leaveSchedule"
  />

  <ConfirmView
    v-else-if="stage === 'confirm' && session"
    :participant="session.participant"
    :exam="session.exam"
    :session="session.session"
    :auth-token="session.token"
    :busy="busy"
    :error="loginError"
    @start="confirmStart"
    @cancel="cancelConfirm"
  />

  <ExamView
    v-else-if="stage === 'exam' && view && session"
    :key="view.attempt.id"
    :token="session.token"
    :participant="session.participant"
    :initial="view"
    @submitted="handleSubmitted"
    @unauthorized="handleUnauthorized"
  />

  <ResultView
    v-else-if="stage === 'result' && view && session"
    :initial="view"
    :participant="session.participant"
    @logout="logout"
    @schedule="showMySchedule"
  />

  <main v-else-if="stage === 'error'" id="main-content" class="screen-center">
    <div class="boot-card boot-card--error">
      <div class="brand-mark brand-mark--large" aria-hidden="true">!</div>
      <h1>Runtime tidak dapat dijangkau</h1>
      <p>{{ bootError }}</p>
      <p class="muted">Pastikan server CBT berjalan, lalu muat ulang halaman ini.</p>
      <button class="button button--primary" type="button" @click="bootstrap">Coba lagi</button>
    </div>
  </main>

  <div v-else class="screen-center" role="status">
    <p>Memuat…</p>
  </div>
</template>
